import { useId, useMemo, useState } from "react";
import { ChevronRightIcon, SearchIcon } from "lucide-react";

import { CopyButton } from "~/components/copy-button";
import { Input } from "~/components/ui/input";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "~/components/ui/collapsible";
import { cn } from "~/lib/utils";

/**
 * A jsonb column, read by a human.
 *
 * Two jobs, and they pull in opposite directions. On a transaction it is the
 * escape hatch for whatever the structured sections above did not lift out --
 * the answer of last resort, so it stays collapsed. On a webhook event the raw
 * payload IS the page: it is the artifact someone pastes into a DurianPay
 * support thread, and burying it behind a click is burying the point. Hence
 * `defaultOpen`.
 *
 * Highlighting is done here rather than with a library. The input is already a
 * serialised string with known structure, so one regex over it is enough, and a
 * syntax-highlighting dependency would be the largest thing in the bundle for
 * the benefit of colouring three token types.
 */
export function JsonViewer({
  value,
  label = "Show raw JSON",
  defaultOpen = false,
  searchable = false,
}: {
  value: unknown;
  /** The trigger's text. Name the column when a page shows more than one. */
  label?: string;
  /** Open on mount. For the blob that is the reason the page exists. */
  defaultOpen?: boolean;
  /** Adds a filter box. Worth it for payloads deep enough to scroll. */
  searchable?: boolean;
}) {
  const [query, setQuery] = useState("");
  const searchId = useId();

  const text = useMemo(
    () => (value == null ? "" : JSON.stringify(value, null, 2)),
    [value],
  );

  // Line-wise filtering, not a tree walk. A JSON tree filter has to decide
  // whether a matching parent implies its children and vice versa, and every
  // answer to that is wrong for some search; a grep over the pretty-printed
  // text is what people expect and can predict.
  const lines = useMemo(() => {
    const all = text.split("\n");
    if (!query.trim()) return { rendered: all, matched: all.length, filtered: false };
    const needle = query.toLowerCase();
    const rendered = all.filter((line) => line.toLowerCase().includes(needle));
    return { rendered, matched: rendered.length, filtered: true };
  }, [text, query]);

  if (value == null || (typeof value === "object" && Object.keys(value).length === 0)) {
    return <p className="text-sm text-muted-foreground">Empty.</p>;
  }

  return (
    <Collapsible defaultOpen={defaultOpen}>
      <div className="flex flex-wrap items-center gap-1">
        <CollapsibleTrigger className="group inline-flex items-center gap-1.5 rounded-md py-1 text-sm font-medium transition-colors hover:text-foreground focus-visible:ring-3 focus-visible:ring-ring/50 focus-visible:outline-none data-panel-open:text-foreground">
          <ChevronRightIcon className="size-4 transition-transform group-data-panel-open:rotate-90" />
          {label}
        </CollapsibleTrigger>
        {/* The whole blob, not the filtered view: what goes on the clipboard is
            what arrived, or the support thread gets a doctored payload. */}
        <CopyButton value={text} label="raw JSON" />
      </div>

      <CollapsibleContent>
        {searchable ? (
          <div className="mt-2 flex items-center gap-2">
            <div className="relative min-w-0 flex-1">
              <SearchIcon className="pointer-events-none absolute top-1/2 left-2.5 size-3.5 -translate-y-1/2 text-muted-foreground" />
              <Input
                id={searchId}
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Filter lines…"
                aria-label="Filter JSON lines"
                className="h-8 pl-8 font-mono text-xs"
              />
            </div>
            {lines.filtered ? (
              <span className="shrink-0 text-xs text-muted-foreground tabular-nums">
                {lines.matched} {lines.matched === 1 ? "line" : "lines"}
              </span>
            ) : null}
          </div>
        ) : null}

        <pre className="mt-2 max-h-[28rem] overflow-auto rounded-lg border bg-muted/40 p-3 font-mono text-xs leading-relaxed">
          {lines.filtered && lines.matched === 0 ? (
            <span className="text-muted-foreground">No line matches “{query}”.</span>
          ) : (
            lines.rendered.map((line, index) => (
              <div key={index} className={cn(lines.filtered && "whitespace-pre")}>
                {highlight(line, query)}
              </div>
            ))
          )}
        </pre>
      </CollapsibleContent>
    </Collapsible>
  );
}

/**
 * Splits one line of pretty-printed JSON into coloured spans.
 *
 * Order matters: keys are strings too, so the key pattern has to win first, and
 * it is recognised by the colon that follows it. Anything unmatched is left as
 * plain text -- punctuation, indentation, and any shape this misses -- so a
 * pattern that fails degrades to an uncoloured line rather than a lost one.
 */
const TOKENS =
  /("(?:[^"\\]|\\.)*"\s*:)|("(?:[^"\\]|\\.)*")|(\b-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?\b)|(\b(?:true|false|null)\b)/g;

function highlight(line: string, query: string) {
  const parts: React.ReactNode[] = [];
  let last = 0;

  for (const match of line.matchAll(TOKENS)) {
    const index = match.index ?? 0;
    if (index > last) parts.push(mark(line.slice(last, index), query, `t${last}`));

    const [raw, key, str, num, literal] = match;
    const className = key
      ? "text-foreground font-medium"
      : str
        ? "text-success"
        : num
          ? "text-info"
          : literal
            ? "text-warning"
            : undefined;

    parts.push(
      <span key={index} className={className}>
        {mark(raw, query, `m${index}`)}
      </span>,
    );
    last = index + raw.length;
  }

  if (last < line.length) parts.push(mark(line.slice(last), query, `t${last}`));
  return parts;
}

/** Wraps occurrences of the search term so a match is findable in a long line. */
function mark(text: string, query: string, keyPrefix: string) {
  const needle = query.trim();
  if (!needle) return text;

  const parts: React.ReactNode[] = [];
  const lower = text.toLowerCase();
  const target = needle.toLowerCase();
  let from = 0;

  for (;;) {
    const at = lower.indexOf(target, from);
    if (at === -1) break;
    if (at > from) parts.push(text.slice(from, at));
    parts.push(
      <mark key={`${keyPrefix}-${at}`} className="rounded-sm bg-warning/30 text-inherit">
        {text.slice(at, at + target.length)}
      </mark>,
    );
    from = at + target.length;
  }

  if (parts.length === 0) return text;
  if (from < text.length) parts.push(text.slice(from));
  return parts;
}
